import { useContext, useEffect, useState } from "react";
import axios from "axios";
import UserContext from "../../contexts/UserContext";
import Utils from "../../Utils";
import _ from "lodash";

const INVALID_HOTEL_MESSAGES = [
  "Please select a valid approved hotel.",
  "Access denied for selected hotel.",
  "Selected hotel is not approved.",
];

const ManagePhotoshootsSection = ({ selectedHotelId }) => {
  const { siteStatuses } = useContext(UserContext);
  const [photoshoots, setPhotoshoots] = useState([]);
  const hasSelectedHotel = Utils.hasBrand();
  const storedUserData = Utils.getUserData() || {};
  const selectedHotelName = storedUserData.hotel_name || "Selected Hotel";

  const fetchPhotoshoots = () => {
    axios
      .get(Utils.apiUrl("photoshoot/listing"), Utils.apiHeaders())
      .then((res) => {
        if (res.data.success) {
          setPhotoshoots(
            (res.data.data || []).filter(
              (record) => Number(record.hotel_id) === selectedHotelId,
            ),
          );
          return;
        }

        if (INVALID_HOTEL_MESSAGES.includes(res.data.message)) {
          Utils.clearSelectedHotel();
          window.location.reload();
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!hasSelectedHotel) {
      setPhotoshoots([]);
      return;
    }

    fetchPhotoshoots();
  }, [hasSelectedHotel, selectedHotelId]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getAttrArr = (hotelAttrs) => {
    if (!hotelAttrs) return [];
    return hotelAttrs.split("#");
  };

  const renderBell = (status) => {
    const redStatus = [1, 2, 3, 5, 11];
    return (
      <div>
        <i
          className={`fas fa-bell text-${redStatus.includes(Number(status)) ? "red" : "green"}-500`}
        ></i>{" "}
        {siteStatuses.statusToBe[Number(status) + 1]}
      </div>
    );
  };

  const renderBellToDo = (st) => {
    let todoBell = Utils.todoBell("HR", st);
    if (todoBell[1] === false) {
      return <></>;
    } else {
      return (
        <div className="flex items-center space-x-1">
          <i
            className={`fas fa-bell text-${todoBell[2] === 1 ? "red" : "green"}-500`}
          ></i>{" "}
          <div className={`statusLabel statusLabel1`}>{todoBell[1]}</div>
        </div>
      );
    }
  };

  const hotelTypes = {
    new: "New Property",
    converted: "Converted Property",
    established: "Established Property",
  };

  const getPhotoshootTypeName = (record) => {
    let type = record.type;
    if (record.type === "GEN") {
      if (record.lifestyle > 0 && record.architectural === 0) type = "LIF";
      else if (record.lifestyle == 0 && record.architectural > 0) type = "ARC";
      else if (record.lifestyle == 0 && record.architectural == 0) type = "PPI";
    }
    return Utils.getPhotoshootTypeName(type);
  };

  const hasActivePhotoshoots = () => {
    return photoshoots.filter((record) => record.status < 11).length > 0;
  };

  return (
    <div className="dashboard-section">
      <div className="section-title">
        <i className="fas fa-camera-retro"></i>
        {`Manage Photoshoots${hasSelectedHotel ? ` for ${selectedHotelName}` : ""}`}
      </div>

      {!hasSelectedHotel && (
        <div className="rounded bg-gray-100 p-3 text-center text-gray-600">
          Select a Hotel to show Photoshoots
        </div>
      )}

      {hasSelectedHotel && photoshoots.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
          <p className="text-sm text-gray-600">
            No photoshoots found for the selected hotel.
          </p>
          <a
            href="photoshoot-request"
            className="btn mt-4 inline-flex items-center justify-center rounded px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <i className="fa-regular fa-camera mr-2"></i>
            Photoshoot Request
          </a>
        </div>
      )}

      {hasSelectedHotel && photoshoots.length > 0 && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {photoshoots.map((record) => {
            const hotelAttrs = getAttrArr(record.hotel_attrs);

            return (
              <article key={record.id} className="project-tile bg-white">
                <div className="bg-white p-4">
                  <div className="flex items-start justify-between">
                    <div className="w-full">
                      <h2 className="text-lg font-semibold text-gray-800">{`${hotelAttrs[0] || ""} - ${hotelAttrs[1] || ""}`}</h2>
                      <div className="mt-1 flex justify-between items-center">
                        <p className=" text-sm text-gray-500">
                          Type:{" "}
                          <span className="font-medium text-gray-700">
                            {Utils.getPhotoshootHotelTypeName(
                              record.hotel_type,
                            )}
                          </span>
                        </p>
                        <div className="text-end bg-gray-100 text-sm text-gray-500 rounded text-center text-capitalize p-1 inline-block">
                          {getPhotoshootTypeName(record)}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        Shoot Date:{" "}
                        <span className="font-medium text-gray-700">
                          {formatDate(record.date_from)}{" "}
                          {!_.isNull(record.date_to) &&
                            ` - ${formatDate(record.date_to)}`}
                        </span>
                      </p>
                      {!_.isNull(record.photographer_name) && (
                        <p className="text-sm text-gray-500">
                          Photographer:{" "}
                          <span className="font-medium text-gray-700">
                            {record.photographer_name}
                          </span>
                        </p>
                      )}
                      <p className="text-sm text-gray-500">
                        Status:{" "}
                        <span className="font-medium text-gray-700">
                          {siteStatuses.status[record.status]}
                        </span>
                        {renderBellToDo(record.hr_todo)}
                      </p>
                    </div>
                  </div>

                  {record.status === 0 && (
                    <div className="mt-4 flex space-x-3 text-xs">
                      <button
                        disabled
                        className="inline-flex cursor-not-allowed items-center justify-center rounded bg-gray-400 px-4 py-2 text-white disabled"
                      >
                        <i className="fas fa-eye mr-2"></i> View Details
                      </button>
                    </div>
                  )}

                  {record.status > 0 && (
                    <div className="mt-4 flex justify-end space-x-3 text-xs">
                      <a
                        href={`photoshoot-details/${record.id}`}
                        className="btn inline-flex items-center justify-center rounded bg-gray-800 px-4 py-2 text-white hover:bg-gray-700"
                      >
                        <i className="fas fa-eye mr-2"></i> View Details
                      </a>
                    </div>
                  )}
                </div>
              </article>
            );
          })}

          {!hasActivePhotoshoots() && (
            <article key="newArticle" className="project-tile bg-white">
              <div className="bg-white p-4 text-center py-8 my-8">
                <p className="text-sm text-gray-600">
                  No active photoshoots found for the selected hotel.
                </p>
                <a
                  href="photoshoot-request"
                  className="btn mt-4 inline-flex items-center justify-center rounded px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                >
                  <i className="fa-regular fa-camera mr-2"></i>
                  Photoshoot Request
                </a>
              </div>
            </article>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagePhotoshootsSection;
